import React, { useState } from 'react';
import {
    Card, CardContent, Grid, Typography, Box, TextField, Button, Table,
    TableHead, TableBody, TableRow, TableCell, IconButton, Chip,
} from '@mui/material';
import FeatherIcon from 'feather-icons-react';
import { useCollection } from 'react-firebase-hooks/firestore';
import { collection, query, orderBy, addDoc, deleteDoc, doc } from '@firebase/firestore';
import PageContainer from '../../../../components/container/PageContainer';
import Breadcrumb from '../../../../layouts/full-layout/breadcrumb/Breadcrumb';
import { db } from '../../../../utils/firebase';
import { loadIngredientAliases } from '../../utils/post';

// Built-in aliases hardcoded in post.js — shown as read-only baselines.
const BUILTIN_ALIASES = {
  'spinach leaves': 'spinach',
  'baby spinach': 'spinach',
  'rocket leaves': 'rocket',
  'arugula': 'rocket',
  'cilantro': 'coriander',
  'scallions': 'spring onion',
  'green onion': 'spring onion',
  'shallot': 'spring onion',
  'courgette': 'zucchini',
  'aubergine': 'eggplant',
  'capsicum': 'bell pepper',
  'prawn': 'shrimp',
  'prawns': 'shrimp',
};

export default function IngredientAliases() {
    const q = query(collection(db, 'ingredientAliases'), orderBy('from'));
    const [snapshot, loading, error] = useCollection(q);
    const [fromVal, setFromVal] = useState('');
    const [toVal, setToVal] = useState('');
    const [busy, setBusy] = useState(false);

    const handleAdd = async () => {
        const cleanFrom = fromVal.trim().toLowerCase();
        const cleanTo = toVal.trim().toLowerCase();
        if (!cleanFrom || !cleanTo || cleanFrom === cleanTo) return;
        setBusy(true);
        try {
            await addDoc(collection(db, 'ingredientAliases'), { from: cleanFrom, to: cleanTo });
            setFromVal('');
            setToVal('');
            // Ingredient matching (recipe checklists, pantry %, Plan) reads a
            // module-level cache built once at login — without this, a new
            // row wouldn't actually take effect until the next login/reload,
            // despite this page's own copy claiming otherwise.
            await loadIngredientAliases(db);
        } finally {
            setBusy(false);
        }
    };

    const handleDelete = async (id) => {
        await deleteDoc(doc(db, 'ingredientAliases', id));
        await loadIngredientAliases(db);
    };

    // Merge built-ins with Firestore rows for display.
    const firestoreRows = snapshot ? snapshot.docs.map(d => ({ id: d.id, ...d.data() })) : [];
    const firestoreFromSet = new Set(firestoreRows.map(r => r.from));
    const builtinRows = Object.entries(BUILTIN_ALIASES)
        .filter(([from]) => !firestoreFromSet.has(from))
        .map(([from, to]) => ({ id: null, from, to, builtin: true }));
    const allRows = [
        ...firestoreRows.map(r => ({ ...r, builtin: false })),
        ...builtinRows,
    ].sort((a, b) => a.from.localeCompare(b.from));

    return (
        <PageContainer title="Admin - Spenza" description="Ingredient aliases admin page.">
            <Breadcrumb title="Ingredient Aliases" subtitle="Admin Tools" />
            <Grid container spacing={0}>
                <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        Aliases let the app treat two ingredient names as the same thing —
                        e.g. &quot;spinach leaves&quot; → &quot;spinach&quot;. Built-in aliases are tagged
                        &quot;built-in&quot; and cannot be deleted here; add a Firestore row with
                        the same &quot;from&quot; name to override one. New rows take effect immediately.
                    </Typography>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                <TextField
                                    size="small"
                                    label="From (e.g. rocket leaves)"
                                    value={fromVal}
                                    onChange={(e) => setFromVal(e.target.value)}
                                    sx={{ flex: 1, minWidth: 200 }}
                                />
                                <TextField
                                    size="small"
                                    label="To / canonical (e.g. rocket)"
                                    value={toVal}
                                    onChange={(e) => setToVal(e.target.value)}
                                    sx={{ flex: 1, minWidth: 200 }}
                                />
                                <Button variant="contained" color="primary" onClick={handleAdd} disabled={busy}>
                                    Add
                                </Button>
                            </Box>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><Typography variant="h6">From</Typography></TableCell>
                                        <TableCell><Typography variant="h6">→ Canonical</Typography></TableCell>
                                        <TableCell align="right"><Typography variant="h6">Actions</Typography></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loading && <TableRow><TableCell colSpan={3}>Loading…</TableCell></TableRow>}
                                    {error && <TableRow><TableCell colSpan={3}>Error loading.</TableCell></TableRow>}
                                    {allRows.map((r) => (
                                        <TableRow key={r.from}>
                                            <TableCell>
                                                {r.from}
                                                <Chip
                                                    size="small"
                                                    variant="outlined"
                                                    label={r.builtin ? 'built-in' : 'custom'}
                                                    color={r.builtin ? undefined : 'primary'}
                                                    sx={{ ml: 1, color: r.builtin ? 'text.secondary' : undefined }}
                                                />
                                            </TableCell>
                                            <TableCell>{r.to}</TableCell>
                                            <TableCell align="right">
                                                {!r.builtin && (
                                                    <IconButton size="small" color="error" onClick={() => handleDelete(r.id)}>
                                                        <FeatherIcon icon="trash-2" width={18} />
                                                    </IconButton>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </PageContainer>
    );
}
